import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight } from 'lucide-react';
import type { StudioId } from '@/lib/studios';
import { useToolCatalog, type CatalogTool } from '@/lib/tool-catalog';
import { cn } from '@/lib/utils';

// Every tool as a labelled row, grouped by what it is for (owner 2026-10-16:
// the pay-per-file tools sit with the others, not in a "quick tools" folder):
// a real output, the name, what it makes, and what it costs (teal = free,
// amber = costs). Used by the phone tab bar's Create sheet, the menu sheet,
// Projects and the studio pages. Nothing here is locked: a tool that cannot
// render right now still opens, and only its paid button is greyed.
export function StudioList({
  className,
  onPick,
  only,
  dense = false,
  replace = false,
}: {
  className?: string;
  onPick?: () => void;
  /** Only these studios; 'quick' stands for every pay-per-file tool. */
  only?: readonly StudioId[];
  dense?: boolean;
  /** Replace the current history entry (the Create sheet's own entry). */
  replace?: boolean;
}) {
  const { t } = useTranslation();
  const keep = (tool: CatalogTool) => !only || (tool.studio ? only.includes(tool.studio) : only.includes('quick'));
  const catalog = useToolCatalog()
    .map((cat) => ({ ...cat, tools: cat.tools.filter(keep) }))
    .filter((cat) => cat.tools.length);
  const row = (tool: CatalogTool) => (
    <li key={tool.id}>
      <Link
        to={tool.path}
        replace={replace}
        onClick={onPick}
        data-testid={`studio-link-${tool.studio ?? tool.id}`}
        className={cn(
          'group flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] transition-colors hover:border-primary/40 hover:bg-white/[0.06]',
          dense ? 'p-2' : 'p-2.5',
        )}
      >
        <img
          src={tool.poster}
          alt=""
          width={dense ? 44 : 56}
          height={dense ? 44 : 56}
          loading="lazy"
          decoding="async"
          className={cn('shrink-0 rounded-xl object-cover', dense ? 'h-11 w-11' : 'h-14 w-14')}
        />
        <span className="min-w-0 flex-1">
          <span className="block truncate font-display text-[15px] font-semibold text-foreground">{tool.name}</span>
          {!dense && <span className="block truncate text-[13px] text-muted-foreground">{tool.line}</span>}
        </span>
        <span
          className={cn(
            'shrink-0 rounded-full px-2.5 py-1 text-[12px] font-semibold ring-1 ring-inset',
            tool.free ? 'bg-accent/10 text-accent ring-accent/30' : 'bg-primary/10 text-primary ring-primary/30',
          )}
        >
          {tool.price}
        </span>
        <ArrowUpRight aria-hidden className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-primary" />
      </Link>
    </li>
  );
  return (
    <div className={cn('grid gap-4', className)} data-testid="studio-list">
      {catalog.map((cat) => (
        <section key={cat.key} aria-label={t(`toolCats.${cat.key}`)}>
          {catalog.length > 1 && (
            <p className="mb-2 font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">{t(`toolCats.${cat.key}`)}</p>
          )}
          <ul className="grid gap-2">{cat.tools.map(row)}</ul>
        </section>
      ))}
    </div>
  );
}
